import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  type OnChanges,
  type OnDestroy,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import { BehaviorSubject, distinctUntilChanged, tap } from 'rxjs';
import { SubSink } from 'subsink';
import type { CreateEduExpenseCategory } from '../../../shared/models/api/edu-expense-category.model';
import { BudgetType } from '../../../shared/models/api/edu-expense.model';
import type { ControlsOf } from '../../../shared/models/controls-of.model';
import type { NgChanges } from '../../../shared/models/ng-changes.model';
import { EduExpenseCategoryService } from '../../../shared/services/api/edu-expense-category.service';
import { FormIdService } from '../../../shared/services/form-id.service';
import { OpaValidators } from '../../../shared/validators/opa.validators';
import { budgetTypeTranslation } from '../../edu-expense-create/edu-expense-create.helpers';
import type { CreateEduExpenseCategoryFormValue } from './edu-expense-category-create.models';

@Component({
  selector: 'opa-edu-expense-category-create',
  imports: [
    CommonModule,
    PorscheDesignSystemModule,
    ReactiveFormsModule,
    LetDirective,
    TranslocoPipe,
  ],
  templateUrl: './edu-expense-category-create.component.html',
  styleUrl: './edu-expense-category-create.component.scss',
})
export class EduExpenseCategoryCreateComponent implements OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly eduExpenseCategoryService = inject(EduExpenseCategoryService);
  private readonly toastManager = inject(ToastManager);

  readonly formId = inject(FormIdService).getId();
  readonly budgetTypes = Object.values(BudgetType);
  readonly budgetTypeTranslation = budgetTypeTranslation;

  @Input({ required: true }) open = false;
  @Output() dismiss = new EventEmitter<boolean | undefined>();

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  private readonly submitting = new BehaviorSubject(false);
  readonly submitting$ = this.submitting.pipe(distinctUntilChanged());

  ngOnChanges(changes: NgChanges<EduExpenseCategoryCreateComponent>): void {
    if (changes.open && this.open) this.form.reset();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  close(success?: boolean): void {
    this.dismiss.emit(success);
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const category = this.formValueToCategory(formValue);
    this.subSink.sink = this.eduExpenseCategoryService
      .createEduExpenseCategory(category)
      .pipe(
        tap({
          next: (createCategory) => {
            this.toastManager.addMessage({
              text: `Category '${createCategory.description}' created`,
              state: 'success',
            });
            this.submitting.next(false);
            this.close(true);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreateEduExpenseCategoryFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreateEduExpenseCategoryFormValue>>({
      budgetType: this.formBuilder.control(null, OpaValidators.required()),
      name: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.isNormalized(),
      ]),
      description: this.formBuilder.control(null, OpaValidators.required()),
    });

    return form;
  }

  private formValueToCategory(form: CreateEduExpenseCategoryFormValue): CreateEduExpenseCategory {
    const category: CreateEduExpenseCategory = {
      description: form.description!,
      budgetType: form.budgetType!,
      name: form.name!,
    };
    return category;
  }
}
